import React, { useState, useMemo } from 'react'
import { Button } from 'components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from 'components/ui/dialog'
import { Library } from 'lucide-react'
import type { LineItemLibraryModalProps, LineItemOption } from '../types/financials.types'

export const LineItemLibraryModal: React.FC<LineItemLibraryModalProps> = ({
	isOpen,
	onClose,
	availableLineItems,
	addedLineItemIds,
	onAddLineItems,
}) => {
	const [searchQuery, setSearchQuery] = useState('')
	const [selectedItemIds, setSelectedItemIds] = useState<string[]>([])
	const [expandedCategories, setExpandedCategories] = useState<string[]>([])

	// Filter line items based on search query
	const filteredCategories = useMemo(() => {
		if (!searchQuery) return availableLineItems

		const query = searchQuery.toLowerCase()
		return availableLineItems
			.map(category => ({
				id: category.id,
				label: category.label,
				items: category.items.filter(
					item =>
						item.label.toLowerCase().includes(query) || item.description?.toLowerCase().includes(query)
				),
			}))
			.filter(category => category.items.length > 0)
	}, [availableLineItems, searchQuery])

	// Auto-expand categories when searching
	React.useEffect(() => {
		if (searchQuery) {
			setExpandedCategories(filteredCategories.map(cat => cat.id))
		} else {
			setExpandedCategories([])
		}
	}, [searchQuery, filteredCategories])

	const toggleCategory = (categoryId: string) => {
		setExpandedCategories(prev =>
			prev.includes(categoryId) ? prev.filter(id => id !== categoryId) : [...prev, categoryId]
		)
	}

	const toggleItemSelection = (itemId: string) => {
		setSelectedItemIds(prev =>
			prev.includes(itemId) ? prev.filter(id => id !== itemId) : [...prev, itemId]
		)
	}

	const handleAddSelected = () => {
		onAddLineItems(selectedItemIds)
		setSelectedItemIds([])
		setSearchQuery('')
		onClose()
	}

	const handleCancel = () => {
		setSelectedItemIds([])
		setSearchQuery('')
		onClose()
	}

	return (
		<Dialog open={isOpen} onClose={handleCancel}>
			<DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
				<DialogHeader>
					<div className="flex items-center gap-3">
						<div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#0080fe]/10 text-[#0080fe]">
							<Library className="w-5 h-5" />
						</div>
						<div>
							<DialogTitle>Add Income Statement Line Items</DialogTitle>
							<DialogDescription>Browse and add line items from the library</DialogDescription>
						</div>
					</div>
				</DialogHeader>

				<div className="flex-1 overflow-y-auto px-5 py-4">
					{/* Search Bar */}
					<div className="mb-4">
						<div className="relative">
							<div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
								<svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
									/>
								</svg>
							</div>
							<input
								type="text"
								placeholder="Search line items..."
								value={searchQuery}
								onChange={e => setSearchQuery(e.target.value)}
								className="
									block w-full rounded-md border-gray-300
									pl-10 pr-3 py-2
									focus:border-blue-500 focus:ring-blue-500
								"
							/>
						</div>
					</div>

					{/* Categories and Items */}
					<div className="space-y-2 max-h-96 overflow-y-auto">
						{filteredCategories.length === 0 ? (
							<div className="text-center py-8 text-gray-500">
								<p>No line items found matching "{searchQuery}"</p>
							</div>
						) : (
							filteredCategories.map(category => {
								const isExpanded = expandedCategories.includes(category.id)

								return (
									<div key={category.id} className="border border-gray-200 rounded-lg">
										{/* Category Header */}
										<button
											onClick={() => toggleCategory(category.id)}
											className="
												w-full flex items-center justify-between
												px-4 py-3 text-left
												hover:bg-gray-50 transition-colors
											"
										>
											<span className="font-medium text-gray-900">{category.label}</span>
											<svg
												className={`w-5 h-5 text-gray-500 transition-transform ${
													isExpanded ? 'transform rotate-180' : ''
												}`}
												fill="none"
												viewBox="0 0 24 24"
												stroke="currentColor"
											>
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													strokeWidth={2}
													d="M19 9l-7 7-7-7"
												/>
											</svg>
										</button>

										{/* Category Items */}
										{isExpanded && (
											<div className="px-4 pb-3 space-y-2">
												{category.items.map(item => {
													const isAlreadyAdded = addedLineItemIds.includes(item.id)
													const isSelected = selectedItemIds.includes(item.id)

													return (
														<label
															key={item.id}
															className={`
																flex items-start space-x-3 p-2 rounded
																${isAlreadyAdded ? 'bg-gray-50 cursor-not-allowed' : 'hover:bg-gray-50 cursor-pointer'}
															`}
														>
															<input
																type="checkbox"
																checked={isAlreadyAdded || isSelected}
																disabled={isAlreadyAdded}
																onChange={() => toggleItemSelection(item.id)}
																className="
																	mt-0.5 h-4 w-4 rounded border-gray-300
																	text-blue-600 focus:ring-blue-500
																	disabled:cursor-not-allowed
																"
															/>
															<div className="flex-1 min-w-0">
																<div className="flex items-center justify-between">
																	<span
																		className={`text-sm font-medium ${
																			isAlreadyAdded ? 'text-gray-400' : 'text-gray-900'
																		}`}
																	>
																		{item.label}
																	</span>
																	{isAlreadyAdded && (
																		<span className="text-xs text-gray-500 ml-2">
																			Already Added
																		</span>
																	)}
																</div>
																{item.description && (
																	<p className="text-xs text-gray-500 mt-0.5">
																		{item.description}
																	</p>
																)}
															</div>
														</label>
													)
												})}
											</div>
										)}
									</div>
								)
							})
						)}
					</div>

					{/* Helper Text */}
					<div className="mt-4 p-3 bg-blue-50 rounded-lg">
						<p className="text-sm text-blue-800">
							💡 <strong>Tip:</strong> Select multiple items and click "Add Selected" to add them all at once.
							You can drag items to reorder them in the income statement.
						</p>
					</div>
				</div>

				<DialogFooter className="justify-between">
					<span className="text-sm text-gray-600">
						{selectedItemIds.length} item{selectedItemIds.length !== 1 ? 's' : ''} selected
					</span>
					<div className="flex space-x-3">
						<Button variant="ghost" onClick={handleCancel}>
							Cancel
						</Button>
						<Button
							variant="primary"
							onClick={handleAddSelected}
							disabled={selectedItemIds.length === 0}
						>
							Add Selected ({selectedItemIds.length})
						</Button>
					</div>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
